<template>
  <div class="fixed bottom-6 right-6 z-50">
    <!-- Floating Chat Button -->
    <div v-if="!isOpen" class="relative">
      <button
        @click="toggleChat"
        class="btn btn-circle btn-primary btn-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-6 h-6"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.733 1.343 3.14 3 3.14h1.088L6 18.75v-2.16c0-.771.625-1.396 1.396-1.396H20.25a2.25 2.25 0 0 0 2.25-2.25V6.75a2.25 2.25 0 0 0-2.25-2.25H5.396a2.25 2.25 0 0 0-2.25 2.25v4.775Z"
          />
        </svg>
      </button>
      <!-- Notification badge -->
      <div
        class="absolute -top-2 -right-2 bg-accent text-accent-content text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse"
      >
        💬
      </div>
    </div>

    <!-- Chat Interface -->
    <div
      v-if="isOpen"
      class="bg-base-100 rounded-lg shadow-2xl w-full max-w-96 h-[90vh] max-h-[500px] flex flex-col border border-base-300"
    >
      <!-- Header -->
      <div
        class="bg-primary text-primary-content p-4 rounded-t-lg flex justify-between items-center"
      >
        <div>
          <h3 class="font-semibold">Asistente Soleando</h3>
          <p class="text-sm opacity-90">¿En qué puedo ayudarte?</p>
        </div>
        <button
          @click="toggleChat"
          class="btn btn-ghost btn-sm btn-circle text-primary-content"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Messages Area -->
      <div ref="messagesContainer" class="flex-1 p-4 overflow-y-auto space-y-3">
        <!-- Welcome Message -->
        <div v-if="messages.length === 0" class="chat chat-start">
          <div class="chat-bubble chat-bubble-primary">
            ¡Hola! Soy el asistente virtual de Soleando. Puedo ayudarte con
            preguntas sobre nuestros viajes, reservas, seguros y más. ¿Qué te
            gustaría saber?
          </div>
        </div>

        <!-- Messages -->
        <div
          v-for="(message, index) in messages"
          :key="index"
          :class="['chat', message.isUser ? 'chat-end' : 'chat-start']"
        >
          <div
            :class="[
              'chat-bubble',
              message.isUser ? 'chat-bubble-info' : 'chat-bubble-secondary',
            ]"
          >
            {{ message.text }}
          </div>
        </div>

        <!-- Typing indicator -->
        <div v-if="isTyping" class="chat chat-start">
          <div class="chat-bubble chat-bubble-secondary">
            <div class="flex space-x-1">
              <div class="w-2 h-2 bg-current rounded-full animate-bounce"></div>
              <div
                class="w-2 h-2 bg-current rounded-full animate-bounce"
                style="animation-delay: 0.1s"
              ></div>
              <div
                class="w-2 h-2 bg-current rounded-full animate-bounce"
                style="animation-delay: 0.2s"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Suggestions -->
      <div v-if="showSuggestions" class="p-3 border-t border-base-300">
        <p class="text-sm text-base-content/70 mb-2">Preguntas frecuentes:</p>
        <div class="flex flex-wrap gap-1">
          <button
            v-for="suggestion in quickSuggestions"
            :key="suggestion"
            @click="sendMessage(suggestion)"
            class="btn btn-xs btn-outline"
          >
            {{ suggestion }}
          </button>
        </div>
      </div>

      <!-- Input Area -->
      <div class="p-4 border-t border-base-300">
        <div class="flex gap-2">
          <input
            v-model="userInput"
            @keyup.enter="sendUserMessage"
            type="text"
            placeholder="Escribe tu pregunta..."
            class="input input-bordered flex-1 text-sm"
            :disabled="isTyping"
          />
          <button
            @click="sendUserMessage"
            :disabled="!userInput.trim() || isTyping"
            class="btn btn-primary btn-sm"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// Reactive data
const isOpen = ref(false);
const userInput = ref("");
const messages = ref([]);
const isTyping = ref(false);
const messagesContainer = ref(null);

const showSuggestions = computed(() => messages.value.length === 0);

const quickSuggestions = [
  "Reservas",
  "Seguros",
  "Cancelaciones",
  "Grupos",
  "Documentos",
];

// FAQ Data extracted from faq.vue
const faqData = [
  {
    keywords: ["reserva", "reservar", "hacer reserva", "booking"],
    answer:
      "Puedes hacer una reserva a través de nuestro sitio web, por teléfono o por email. Te enviaremos un contrato de viaje que deberás completar y firmar. Para confirmar tu reserva, necesitarás realizar el pago inicial especificado en el contrato.",
  },
  {
    keywords: ["pago", "pagos", "método pago", "como pagar", "tarjeta"],
    answer:
      "Aceptamos transferencias bancarias, tarjetas de crédito y débito. Los detalles específicos del pago se incluirán en tu contrato de viaje. Normalmente requerimos un depósito inicial para confirmar la reserva y el pago final antes de la fecha de salida.",
  },
  {
    keywords: [
      "cancelar",
      "cancelación",
      "cancelaciones",
      "devolver",
      "reembolso",
    ],
    answer:
      "Las condiciones de cancelación varían según el viaje y se especifican en tu contrato. Recomendamos encarecidamente contratar un seguro de cancelación. Si necesitas cancelar, debes notificárnoslo por email a info@soleando.eu lo antes posible.",
  },
  {
    keywords: ["seguro", "seguros", "seguro viaje", "obligatorio"],
    answer:
      "Sí, es obligatorio. Todos los viajeros deben estar cubiertos por un seguro de viaje que incluya asistencia médica y repatriación. También recomendamos contratar un seguro de cancelación para protegerte en caso de imprevistos.",
  },
  {
    keywords: ["documentos", "documentación", "pasaporte", "visado", "visa"],
    answer:
      "Necesitarás pasaporte en vigor (con al menos 6 meses de validez) y, según el destino, puede requerirse visado. Te informaremos de todos los requisitos específicos para tu destino. También es importante revisar las vacunas recomendadas u obligatorias.",
  },
  {
    keywords: ["vacunas", "vacunación", "medicina tropical"],
    answer:
      "Dependiendo del destino, pueden requerirse ciertas vacunas. Te proporcionaremos información sobre las vacunas recomendadas u obligatorias para tu viaje. Es importante consultar con tu médico o un centro de medicina tropical con suficiente antelación.",
  },
  {
    keywords: ["grupo", "grupos", "tamaño grupo", "cuantas personas"],
    answer:
      "Trabajamos con grupos pequeños para garantizar una experiencia más personalizada y auténtica. El tamaño del grupo varía según el viaje, pero generalmente está entre 6 y 16 personas. Los grupos pequeños nos permiten acceder a lugares únicos y ofrecer un servicio más cercano.",
  },
  {
    keywords: [
      "personalizado",
      "personalizar",
      "medida",
      "privado",
      "personalizo",
    ],
    answer:
      "¡Por supuesto! Ofrecemos viajes completamente personalizados según tus intereses, fechas y presupuesto. Contáctanos para contarnos qué tienes en mente y crearemos un itinerario único para ti o tu grupo.",
  },
  {
    keywords: ["dificultad", "físico", "nivel", "esfuerzo"],
    answer:
      "Ofrecemos viajes para diferentes niveles, desde cómodos y accesibles hasta más aventureros. En cada programa especificamos el nivel de dificultad y los requisitos físicos. Si tienes dudas sobre si un viaje es adecuado para ti, consúltanos sin compromiso.",
  },
  {
    keywords: ["precio", "precios", "incluye", "incluido", "coste"],
    answer:
      "Cada viaje incluye diferentes servicios que se detallan en la ficha técnica. Generalmente incluyen alojamiento, transporte terrestre, guías locales, y algunas comidas. Los vuelos internacionales, visados, seguro y gastos personales no suelen estar incluidos salvo que se especifique.",
  },
  {
    keywords: ["vuelos", "vuelo", "avión", "aéreo"],
    answer:
      "La mayoría de nuestros viajes no incluyen vuelos internacionales para darte flexibilidad en la elección de compañía y horarios. Sin embargo, podemos ayudarte a encontrar las mejores opciones de vuelo y, en algunos casos, incluirlos en el paquete si lo prefieres.",
  },
  {
    keywords: ["equipaje", "llevar", "ropa", "maleta"],
    answer:
      "Te proporcionaremos una lista detallada de equipaje recomendado específica para tu viaje. Incluirá ropa adecuada para el clima, calzado recomendado, artículos de higiene personal, medicamentos y cualquier equipo especial necesario.",
  },
  {
    keywords: ["clima", "tiempo", "temperatura", "calor", "frío"],
    answer:
      "El clima varía mucho según el destino y la época del año. En las fichas de cada viaje incluimos información sobre las temperaturas esperadas y la mejor época para viajar. Te recomendamos traer ropa para diferentes temperaturas, especialmente en destinos desérticos.",
  },
  {
    keywords: ["internet", "wifi", "conexión", "móvil"],
    answer:
      "La disponibilidad de internet varía según el destino. En ciudades principales suele haber wifi en hoteles y restaurantes. En zonas más remotas o durante acampadas en el desierto, la conexión puede ser limitada o inexistente.",
  },
  {
    keywords: [
      "comida",
      "comidas",
      "vegetariano",
      "vegano",
      "celíaco",
      "alergia",
    ],
    answer:
      "Podemos adaptar las comidas para vegetarianos, veganos, celíacos y otras restricciones alimentarias. Es muy importante que nos informes de cualquier alergia o restricción al hacer la reserva para que podamos coordinarlo con nuestros proveedores locales.",
  },
  {
    keywords: ["emergencia", "emergencias", "urgencia", "accidente"],
    answer:
      "Tenemos protocolos establecidos para diferentes tipos de emergencias. Nuestros guías están formados en primeros auxilios y tenemos contacto 24/7 con servicios de emergencia locales. Tu seguro de viaje también proporcionará asistencia en caso de emergencias médicas.",
  },
  {
    keywords: ["contacto", "contactar", "teléfono", "email", "llamar"],
    answer:
      "Puedes contactarnos por email a info@soleando.eu, por teléfono (+34) 608 76 29 62 / (+34) 620 50 17 61, o en nuestra oficina en C/ Arturo Soria, 333 – 28033 Madrid. Nuestro horario es de lunes a viernes de 9:00 a 18:00h.",
  },
];

const fallbackMessage =
  "Gracias por contactar con Soleando. Si no encuentras la respuesta en nuestra WEB contacta con info@soleando.eu";

// Methods
const toggleChat = () => {
  isOpen.value = !isOpen.value;
};

const sendMessage = (text) => {
  userInput.value = text;
  sendUserMessage();
};

const sendUserMessage = () => {
  if (!userInput.value.trim() || isTyping.value) return;

  const userMessage = userInput.value.trim();
  messages.value.push({ text: userMessage, isUser: true });
  userInput.value = "";

  // Show typing indicator
  isTyping.value = true;

  // Simulate thinking time
  setTimeout(() => {
    const response = findBestAnswer(userMessage);
    messages.value.push({ text: response, isUser: false });
    isTyping.value = false;
    scrollToBottom();
  }, 1000);

  scrollToBottom();
};

const findBestAnswer = (question) => {
  const lowerQuestion = question.toLowerCase();

  // Find the best matching FAQ entry
  let bestMatch = null;
  let bestScore = 0;

  for (const faq of faqData) {
    let score = 0;
    for (const keyword of faq.keywords) {
      if (lowerQuestion.includes(keyword.toLowerCase())) {
        score += keyword.length; // Longer keywords get higher priority
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestMatch = faq;
    }
  }

  return bestMatch ? bestMatch.answer : fallbackMessage;
};

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

// Lifecycle
onMounted(() => {
  // Auto scroll to bottom when new messages arrive
  watch(messages, scrollToBottom, { deep: true });
});
</script>

<style scoped>
/* Custom scrollbar for messages */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}
</style>
